import { psmGet, psmPost, psmPut } from "../api/api";
import { getEmpObj } from "../utils/sessionDB";

/**
 * Bug & Enhancement Request Tracker — PSM tickets-tracker APIs.
 * Paths are relative to PSM_BASE_URL (/saas/psm).
 *
 * Service codes:
 *   Add    → PSM03A
 *   Update → PSM03U
 *   Search → PSM03S
 */

function unwrapData(response) {
  if (response == null || typeof response !== "object") return response;
  const inner = response.data;
  if (inner === undefined) return response;
  if (inner && typeof inner === "object") {
    if (
      !Array.isArray(inner) &&
      inner.data !== undefined &&
      typeof inner.data === "object" &&
      inner.data !== null &&
      !Array.isArray(inner.data)
    ) {
      return inner.data;
    }
    return inner;
  }
  // Primitive `data` is often a message string; ticket fields live on the envelope.
  return response;
}

function flattenTicket(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const nestedKeys = ["ticket", "request", "record", "item", "payload"];
  let merged = { ...raw };
  nestedKeys.forEach((key) => {
    const nested = raw[key];
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      merged = { ...merged, ...nested };
    }
  });
  return merged;
}

function coerceText(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    return value.map(coerceText).filter(Boolean).join("\n");
  }
  if (typeof value === "object") {
    return coerceText(
      value.remarks ??
        value.remark ??
        value.comment ??
        value.comments ??
        value.notes ??
        value.text ??
        value.value ??
        value.message
    );
  }
  return "";
}

const REMARKS_KEYS = [
  "remarks",
  "remark",
  "ticketRemarks",
  "ticketRemark",
  "requestRemarks",
  "requestRemark",
  "additionalRemarks",
  "comments",
  "comment",
  "notes",
  "note",
];

/** Read remarks from any API alias the tracker GET/search payload might use. */
export function pickTicketRemarks(ticket) {
  if (!ticket || typeof ticket !== "object") return "";
  for (const key of REMARKS_KEYS) {
    const text = coerceText(ticket[key]);
    if (text.trim()) return text;
  }
  for (const key of Object.keys(ticket)) {
    if (!/remark/i.test(key) || /count|id|at$/i.test(key)) continue;
    const text = coerceText(ticket[key]);
    if (text.trim()) return text;
  }
  return "";
}

function normalizeTicket(raw) {
  const ticket = flattenTicket(unwrapData(raw));
  if (!ticket || typeof ticket !== "object") return ticket;
  return { ...ticket, remarks: pickTicketRemarks(ticket) };
}

const REMARKS_CACHE_KEY = "psm_ticket_remarks";

export function cacheTicketRemarks(requestId, remarks) {
  if (requestId == null || requestId === "") return;
  try {
    const raw = sessionStorage.getItem(REMARKS_CACHE_KEY);
    const map = raw ? JSON.parse(raw) : {};
    map[String(requestId)] = String(remarks ?? "");
    sessionStorage.setItem(REMARKS_CACHE_KEY, JSON.stringify(map));
  } catch {
    // Ignore storage failures (private mode, quota, etc.)
  }
}

export function readCachedTicketRemarks(requestId) {
  if (requestId == null || requestId === "") return "";
  try {
    const raw = sessionStorage.getItem(REMARKS_CACHE_KEY);
    const map = raw ? JSON.parse(raw) : {};
    const value = map[String(requestId)];
    return value != null ? String(value) : "";
  } catch {
    return "";
  }
}

function buildQuery(params = {}) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    searchParams.set(key, String(value));
  });
  const qs = searchParams.toString();
  return qs ? `?${qs}` : "";
}

function normalizePage(raw, fallbackPage = 0, fallbackSize = 10) {
  const data = unwrapData(raw);
  if (Array.isArray(data)) {
    return {
      content: data,
      totalElements: data.length,
      totalPages: 1,
      number: fallbackPage,
      size: fallbackSize,
      last: true,
    };
  }
  const content = Array.isArray(data?.content)
    ? data.content
    : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.records)
        ? data.records
        : [];
  return {
    content,
    totalElements: data?.totalElements ?? data?.total ?? content.length,
    totalPages:
      data?.totalPages ??
      Math.max(1, Math.ceil((data?.totalElements ?? content.length) / (data?.size ?? fallbackSize))),
    number: data?.number ?? data?.page ?? fallbackPage,
    size: data?.size ?? fallbackSize,
    last: data?.last ?? true,
  };
}

export function buildTicketPayload(fields = {}) {
  const remarks = String(fields.remarks ?? fields.remark ?? "").trim();
  const payload = {
    service: fields.service?.trim?.() ?? fields.service ?? "",
    module: fields.module?.trim?.() ?? fields.module ?? "",
    subModule: fields.subModule?.trim?.() ?? fields.subModule ?? "",
    title: fields.title?.trim?.() ?? fields.title ?? "",
    priority: fields.priority?.trim?.() ?? fields.priority ?? "",
    requestType: fields.requestType?.trim?.() ?? fields.requestType ?? "",
    description: fields.description?.trim?.() ?? fields.description ?? "",
    assignee: fields.assignee?.trim?.() ?? fields.assignee ?? "",
    targetRelease: fields.targetRelease?.trim?.() ?? fields.targetRelease ?? "",
    progressPercentage: String(fields.progressPercentage ?? "").trim(),
    remarks,
    remark: remarks,
    createdBy: fields.createdBy?.trim?.() ?? fields.createdBy ?? "",
  };
  if (fields.status != null && String(fields.status).trim() !== "") {
    payload.status = String(fields.status).trim();
  }
  return payload;
}

/** GET /tickets-tracker/search — X-Service-Code: PSM03S */
export async function searchTickets({
  page = 0,
  size = 10,
  sortBy = "requestId",
  sortDir = "DESC",
} = {}) {
  const response = await psmGet(
    `/tickets-tracker/search${buildQuery({ page, size, sortBy, sortDir })}`,
    "PSM03S"
  );
  return normalizePage(response, page, size);
}

/** GET /tickets-tracker/{requestId} — X-Service-Code: PSM03S */
export async function getTicket(requestId) {
  const response = await psmGet(`/tickets-tracker/${requestId}`, "PSM03S");
  return normalizeTicket(response);
}

/** POST /tickets-tracker/add — X-Service-Code: PSM03A */
export async function createTicket(fields) {
  const response = await psmPost("/tickets-tracker/add", buildTicketPayload(fields), "PSM03A");
  return normalizeTicket(response);
}

/** PUT /tickets-tracker/update/{requestId} — X-Service-Code: PSM03U */
export async function updateTicket(requestId, fields) {
  const updatedBy = String(getEmpObj().first_name || "").trim();
  if (!updatedBy) {
    throw new Error("Unable to identify the current user. Please sign in again before updating the ticket.");
  }
  const response = await psmPut(
    `/tickets-tracker/update/${requestId}`,
    { ...buildTicketPayload(fields), updatedBy },
    "PSM03U"
  );
  return normalizeTicket(response);
}

/** GET /tickets-tracker/{requestId}/audit — X-Service-Code: PSM03S */
export async function getTicketAudit(requestId, { page = 0, size = 20 } = {}) {
  const response = await psmGet(
    `/tickets-tracker/${requestId}/audit${buildQuery({ page, size })}`,
    "PSM03S"
  );
  const pageData = normalizePage(response, page, size);
  return {
    items: pageData.content,
    page: pageData.number,
    size: pageData.size,
    totalElements: pageData.totalElements,
    totalPages: pageData.totalPages,
    last: pageData.last,
  };
}
